import { MAX_SLOTS } from '.';
import { ICampaign, IDevice } from '../interfaces';

export const getCampaignsOverlapping = (campaign: ICampaign, otherCampaigns: ICampaign[]) => {
    const res = [];

    res.push(campaign);

    for (const c of otherCampaigns) {
        if (c.id === campaign.id) {
            continue;
        }

        if (c.startDate < campaign.endDate && c.endDate > campaign.startDate) {
            res.push(c);
        }
    }

    return res;
};

export const getMagsNumberFromDevices = (
    devices: number[],
    table: {
        [key: number]: IDevice;
    }
) => {
    const s = new Set();

    for (const d of devices) {
        const dev = table[d];

        if (!dev || !(dev as any).Shop) continue;

        s.add((dev as any).Shop.id);
    }

    return s.size;
};

export interface IBlockConflict {
    start: Date;
    end: Date;
    campaign: ICampaign;
}

const getLocalDeviceOccupation = (reference: IBlockConflict, blocksOverlapping: IBlockConflict[]) => {
    const block_hours: { [key: number]: number[] } = {};

    let day = reference.start.getDay() - 1;
    if (day === -1) day = 6;

    const populateResultWithCampaign = (campaign: ICampaign, h: number) => {
        if (campaign.planning[day].indexOf(h) === -1) {
            return;
        }

        for (const d of campaign.devices) {
            if (d in block_hours) {
                block_hours[d][h - 7] += campaign.slots;
            } else {
                block_hours[d] = new Array(16).fill(0);
                block_hours[d][h - 7] = campaign.slots;
            }
        }
    };

    for (let i = 7; i < 23; i++) {
        populateResultWithCampaign(reference.campaign, i);

        const checkedCampaigns = new Set();
        for (const block of blocksOverlapping) {
            if (checkedCampaigns.has(block.campaign.id)) {
                continue;
            }

            checkedCampaigns.add(block.campaign.id);
            populateResultWithCampaign(block.campaign, i);
        }
    }

    return block_hours;
};

export const getFreeSpaceFromTimeConflicts = (data: IBlockConflict[], start: Date, end: Date, devices: number[]) => {
    const results = [];
    const mockCampaign = {
        planning: new Array(7).fill(new Array(16).map((_, i) => i + 7)),
        active: true,
        advertiser: '',
        brand: '',
        creatives: [],
        devices: [],
        devicesWithSequenceFiles: [],
        endDate: end,
        groupedIds: [],
        id: '',
        name: 'MOCK_CAMPAIGN',
        originalName: 'MOCK_CAMPAIGN',
        signs: {},
        slots: 1,
        startDate: start,
        state: 'other',
        hideFromInterface: false,
        revenue: 0
    };

    do {
        const localDeviceOccupation = getLocalDeviceOccupation(
            {
                start: new Date(start),
                end,
                campaign: mockCampaign
            },
            data.filter((b) => b.start.getDay() === start.getDay())
        );

        for (let h = 7; h <= 22; h++) {
            const devicesOverflowing = [];
            for (const deviceId in localDeviceOccupation) {
                if (localDeviceOccupation[deviceId][h - 7] >= MAX_SLOTS) {
                    devicesOverflowing.push(parseInt(deviceId));
                }
            }

            if (devicesOverflowing.length < devices.length) {
                const bStart = new Date(start);
                bStart.setHours(h);

                const bEnd = new Date(bStart);
                bEnd.setHours(bEnd.getHours() + 1);

                if (h === 7) {
                    bStart.setHours(0);
                    bEnd.setHours(8);
                } else if (h === 22) {
                    bEnd.setHours(0);
                    bEnd.setDate(bEnd.getDate() + 1);
                }

                results.push({
                    start: bStart,
                    end: bEnd,
                    devicesOverflowing,
                    devicesAvailableCount: 0
                });
            }
        }

        start.setDate(start.getDate() + 1);
    } while (start < end);

    // Merge equal dates
    for (let i = 0; i < results.length - 1; i++) {
        if (results[i].devicesOverflowing.length !== results[i + 1].devicesOverflowing.length) {
            continue;
        }

        if (results[i + 1].start.getTime() === results[i].end.getTime()) {
            // Merge
            results[i].end = results[i + 1].end;
            results.splice(i + 1, 1);
            i--;
        }
    }

    for (const r of results) {
        r.devicesAvailableCount = devices.length - r.devicesOverflowing.length;
    }

    return results;
};

export const getCurrentCampaignBlock = (data: IBlockConflict[], currentCampaign: ICampaign) => {
    const results = [];

    const start = new Date(currentCampaign.startDate);
    const end = new Date(currentCampaign.endDate);

    do {
        const endOfDay = new Date(start);
        endOfDay.setDate(endOfDay.getDate() + 1);

        const localDeviceOccupation = getLocalDeviceOccupation(
            {
                start: new Date(start),
                end,
                campaign: currentCampaign
            },
            data.filter((b) => b.end <= endOfDay && b.start >= start && b.start.getDay() === start.getDay())
        );

        let day = start.getDay() - 1;
        if (day === -1) day = 6;

        for (let h = 7; h <= 22; h++) {
            if (currentCampaign.planning[day].indexOf(h) === -1) {
                continue;
            }

            const devicesOverflowing = [];
            for (const deviceId in localDeviceOccupation) {
                if (localDeviceOccupation[deviceId][h - 7] > MAX_SLOTS) {
                    devicesOverflowing.push(parseInt(deviceId));
                }
            }

            const bStart = new Date(start);
            bStart.setHours(h);

            const bEnd = new Date(bStart);
            bEnd.setHours(bEnd.getHours() + 1);

            if (h === 7) {
                bStart.setHours(0);
                bEnd.setHours(8);
            } else if (h === 22) {
                bEnd.setHours(0);
                bEnd.setDate(bEnd.getDate() + 1);
            }

            results.push({
                start: bStart,
                end: bEnd,
                devicesOverflowing
            });
        }

        start.setDate(start.getDate() + 1);
    } while (start < end);

    // Merge equal dates
    for (let i = 0; i < results.length - 1; i++) {
        if (results[i].devicesOverflowing.length !== results[i + 1].devicesOverflowing.length) {
            continue;
        }

        if (results[i + 1].start.getTime() === results[i].end.getTime()) {
            // Merge
            results[i].end = results[i + 1].end;
            results.splice(i + 1, 1);
            i--;
        }
    }

    return results;
};

export const generateDateBlocksForCampaigns = (
    campaigns: ICampaign[],
    referenceStart: Date,
    referenceEnd: Date,
    time_conflicts:
        | {
              start: Date;
              end: Date;
              campaign: ICampaign;
          }[]
        | null
) => {
    const data: { [key: string]: number[][] } = {};

    let i = 1;
    for (const c of campaigns) {
        const start = new Date(Math.max(referenceStart.getTime(), c.startDate.getTime()));
        const end = new Date(Math.min(referenceEnd.getTime(), c.endDate.getTime()));

        let name = `${c.name} (${i})`;
        if (c.state === 'draft') {
            name = `🖊 ${c.name} (${i})`;
        } else if (c.active === false) {
            name = `⏸ ${c.name} (${i})`;
        }

        i++;

        data[name] = [];

        while (start < end) {
            let day = start.getDay() - 1;
            if (day === -1) {
                day = 6;
            }

            const endOfDay = new Date(start);
            endOfDay.setDate(endOfDay.getDate() + 1);

            if (c.planning[day].length >= 16) {
                data[name].push([start.getTime(), endOfDay.getTime()]);
                if (time_conflicts)
                    time_conflicts.push({
                        start: new Date(start),
                        end: endOfDay,
                        campaign: c
                    });
            } else {
                const blocks = [];

                for (let i = 0; i < c.planning[day].length; i++) {
                    let j = i + 1;
                    let end = c.planning[day][i];
                    while (j < c.planning[day].length && c.planning[day][j] === c.planning[day][j - 1] + 1) {
                        end = c.planning[day][j];
                        j++;
                    }

                    if (end === c.planning[day][i]) {
                        blocks.push([c.planning[day][i], c.planning[day][i] + 1]);
                    } else {
                        blocks.push([c.planning[day][i], end + 1]);
                    }

                    i = j - 1;
                }

                for (const b of blocks) {
                    const s = new Date(start);
                    s.setHours(b[0]);

                    const e = new Date(s);
                    e.setHours(b[1]);

                    data[name].push([s.getTime(), e.getTime()]);
                    if (time_conflicts)
                        time_conflicts.push({
                            start: s,
                            end: e,
                            campaign: c
                        });
                }
            }

            start.setDate(start.getDate() + 1);
        }
    }

    // Merge equal dates
    for (const k in data) {
        for (let i = 0; i < data[k].length - 1; i++) {
            if (data[k][i + 1][0] === data[k][i][1]) {
                // Merge
                data[k][i][1] = data[k][i + 1][1];
                data[k].splice(i + 1, 1);
                i--;
            }
        }
    }

    return data;
};
